import { Component, OnInit, Input, Optional } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { MerchantService } from '../../../services/merchant.service';
import { Constants } from '../../../app.constants';
import { AutomationDialogComponent } from '../automation-dialog/automation-dialog.component';
import { AutomationDetailsDialogComponent } from '../automation-details-dialog/automation-details-dialog.component';
import { AutomationPreviewDialogComponent } from '../automation-preview-dialog/automation-preview-dialog.component';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';

@Component({
  selector: 'automations-list',
  templateUrl: './automations-list.component.html',
  styleUrls: ['./automations-list.component.css'],
})
export class AutomationsListComponent implements OnInit {
  @Input() embedded = false;
  automations: any[] = [];
  loading = false;
  skeletonRows = Array.from({ length: 8 }, () => ({ __loading: true }));
  private readonly minLoadingMs = 650;
  private loadingStartedAt = 0;

  constructor(
    private merchantService: MerchantService,
    private dialog: MatDialog,
    private constants: Constants,
    @Optional() private dialogRef: MatDialogRef<AutomationsListComponent>,
  ) {}

  ngOnInit(): void {
    this.loadAutomations();
  }

  loadAutomations(): void {
    this.loading = true;
    this.loadingStartedAt = Date.now();
    this.merchantService
      .getCustomerAutomations()
      .subscribe({
        next: (res: any) => {
          this.automations = res;
        },
        error: (err: any) => {
          console.error('Error loading automations:', err);
          this.constants.snack('Failed to load automations');
        },
      })
      .add(() => {
        const elapsed = Date.now() - this.loadingStartedAt;
        const remaining = Math.max(this.minLoadingMs - elapsed, 0);

        window.setTimeout(() => (this.loading = false), remaining);
      });
  }

  onRowActivate(event: any): void {
    if (event.type === 'click') {
      this.viewAutomationDetails(event.row);
    }
  }

  viewAutomationDetails(automation: any): void {
    const dialogRef = this.dialog.open(AutomationDetailsDialogComponent, {
      data: { automation },
      scrollStrategy: new NoopScrollStrategy(),
      width: '900px',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: false,
      panelClass: 'custom-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadAutomations();
      }
    });
  }

  editAutomation(automation: any): void {
    if (this.isScheduleAutomation(automation)) {
      this.constants.snack(
        'Scheduled automations are temporarily unavailable in the editor',
      );
      return;
    }

    const dialogRef = this.dialog.open(AutomationDialogComponent, {
      data: { automation },
      scrollStrategy: new NoopScrollStrategy(),
      width: '800px',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadAutomations();
      }
    });
  }

  deleteAutomation(automation: any): void {
    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Delete Automation',
        body: `Are you sure you want to delete the automation "${automation.name}"? This action cannot be undone.`,
        option1: 'Cancel',
        option2: 'Delete',
      },
    });

    dialogRef.afterClosed().subscribe((option: number) => {
      if (option === 2) {
        this.merchantService.deleteAutomation(automation._id).subscribe({
          next: () => {
            this.constants.snack('Automation deleted successfully');
            this.loadAutomations();
          },
          error: (err: any) => {
            console.error('Error deleting automation:', err);
            this.constants.snack('Failed to delete automation');
          },
        });
      }
    });
  }

  toggleEnabled(automation: any): void {
    const updatedAutomation = { ...automation, enabled: !automation.enabled };
    this.merchantService
      .updateAutomation(automation._id, updatedAutomation)
      .subscribe({
        next: () => {
          this.constants.snack(
            `Automation ${updatedAutomation.enabled ? 'enabled' : 'disabled'}`,
          );
          this.loadAutomations();
        },
        error: (err: any) => {
          console.error('Error updating automation:', err);
          this.constants.snack('Failed to update automation');
        },
      });
  }

  previewRecipients(automation: any, event: any): void {
    event.stopPropagation(); // Prevent row click
    const dialogRef = this.dialog.open(AutomationPreviewDialogComponent, {
      data: { automation },
      scrollStrategy: new NoopScrollStrategy(),
      width: '800px',
      maxHeight: '90vh',
      autoFocus: false,
      disableClose: false,
      panelClass: 'custom-dialog',
    });
  }

  getTargetDisplay(automation: any): string {
    if (automation.target?.type === 'all_customers') {
      return 'All customers';
    }
    return 'Filtered customers';
  }

  getTriggerDisplay(automation: any): string {
    if (automation.trigger?.type === 'customer_takes_loyalty_card') {
      return 'Customer takes loyalty card';
    }

    return this.getFrequencyDisplay(automation);
  }

  getFrequencyDisplay(automation: any): string {
    const freq = automation.frequency;
    if (!freq?.type) {
      return 'Schedule';
    }

    switch (freq.type) {
      case 'once':
        return 'Once';
      case 'daily':
        return `Daily at ${freq.time || '09:00'}`;
      case 'weekly':
        return `Weekly (${this.getDayName(freq.dayOfWeek)})`;
      case 'monthly':
        return `Monthly (Day ${freq.dayOfMonth})`;
      case 'milestone':
        return `Milestone (${freq.milestoneThreshold} stamps)`;
      default:
        return freq.type;
    }
  }

  isScheduleAutomation(automation: any): boolean {
    return !automation.trigger?.type || automation.trigger.type === 'schedule';
  }

  getDayName(day: number): string {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return days[day] || '';
  }
}
